import { Cart, LineItem } from "@made-people/grease-cart";
import { GtmDataLayerEvent } from "@made-people/grease-gtm";
import {
  Product,
  ProductBase,
  ProductProviderContext,
} from "@made-people/grease-product";
import { FrontendContextV1 } from "@made-people/nestjs-bff-service";

export function gtmReturnId(item: LineItem) {
  const sizecup = item.sku.slice(6).split("x");
  const s = sizecup[0].length === 2 ? "00" + sizecup[0] : "0" + sizecup[0];
  const customid = sizecup[1]
    ? item.product.base.productKey + sizecup[1] + " " + s
    : item.product.base.productKey + sizecup[0];
  return customid;
}

export function gtmReturnIdProduct(
  productBase: ProductBase,
  sizeLabel: string,
) {
  const sizecup = sizeLabel.split(" / ");
  let customidproduct = productBase.productKey + sizecup[0];
  if (sizecup[1]) {
    customidproduct += " " + sizecup[1].padStart(4, "0");
  }
  return customidproduct;
}

export function gtmReturnVariantSku(product: Product) {
  if (product.dataModel === "bundle") {
    return null;
  } else {
    return product.base.productKey.slice(-2);
  }
}

export function gtmProductUrl(
  product: Product,
  frontendContext: FrontendContextV1,
) {
  return (
    frontendContext.baseUrl +
    "/" +
    frontendContext.siteSlug +
    "/" +
    product.base.slug
  );
}

export function gtmProductImage(base: ProductBase) {
  return base.content.media.find((m) => m.type === "image")?.src;
}

export function gtmProductCategory(base: ProductBase) {
  return base.attributes.find(
    (attr) => attr.attributeKey === "product_main_category",
  )?.valueLabel;
}

export function gtmPushCheckoutEvent(
  pusher: (data: GtmDataLayerEvent) => void,
  event: string,
  step: string,
  cart: Cart,
  frontendContext: FrontendContextV1,
) {
  if (!cart) {
    return;
  }
  event = event || "checkoutOption";
  pusher({
    event,
    ecommerce: {
      cartGrandTotal: cart.totals?.grandTotal.number,
      checkout: {
        actionField: { step },
        products: cart.items.map((item) => ({
          name: gtmProductParentId(item.product.base),
          id: gtmReturnId(item),
          parentId: gtmProductParentId(item.product.base),
          productId: item.product.base.productKey,
          price: item.price.priceEach.number,
          category: gtmProductCategory(item.product.base),
          brand: item.product.base.content.brand,
          variant: gtmReturnVariantSku(item.product), // updating this to contain colour code despite history of using size
          quantity: item.quantity,
          image: gtmProductImage(item.product.base),
          title: item.product.base.content.name,
          url: gtmProductUrl(item.product, frontendContext),
        })),
      },
      order: {
        order_id: cart.cartId + Date.now(),
        totalWithShipping: cart.totals?.grandTotal.number,
        totalWithoutShipping:
          (cart.totals?.subtotal.number ?? 0) +
          (cart.totals?.discount?.number ?? 0),
        checkoutUrl:
          frontendContext.baseUrl +
          "/" +
          frontendContext.siteSlug +
          "/checkout",
      },
    },
  });
}

export function gtmProductParentId(base: ProductBase) {
  return base.attributes.find(
    (attr) => attr.attributeKey === "product_parent_id",
  )?.value;
}

export function gtmProductVariantName(base: ProductBase) {
  return (
    base.attributes.find((attr) => attr.attributeKey === "product_variant_name")
      ?.value ?? "variantName_not_set"
  );
}

export function gtmGetCartItem(
  item: LineItem,
  quantity: number,
  frontendContext: FrontendContextV1,
) {
  return {
    id: gtmReturnId(item),
    name: gtmProductParentId(item.product.base),
    parentId: gtmProductParentId(item.product.base),
    productId: item.product.base.productKey,
    price: item.price.priceEach.number,
    category: gtmProductCategory(item.product.base),
    brand: item.product.base.content.brand,
    variant: gtmReturnVariantSku(item.product),
    quantity: quantity,
    image: gtmProductImage(item.product.base),
    title: item.product.base.content.name,
    url: gtmProductUrl(item.product, frontendContext),
  };
}

export function gtmGetListId(context: ProductProviderContext) {
  if (context.type !== "plp") {
    return undefined;
  }

  return (
    // This context.listId is hardcoded in blueprint components, and
    // they need to be turned into something dynamic.
    // context.listId ||
    window.location.pathname.replace(/(^\/[^/]+\/?)|(\/$)/g, "")
  );
}
